import { Button, Drawer, Form, Input, message, Select } from 'antd';
import { useEffect, useState } from 'react';
import plantTypeService from '../../services/plant-type.service';

const { TextArea } = Input;
const { Option } = Select;

function AddEditPlantType({ open, onClose, plantType }) {
  const [form] = Form.useForm();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const isEditing = !!plantType;

  useEffect(() => {
    if (open) {
      if (isEditing && plantType) {
        form.setFieldsValue({
          code: plantType.code,
          name: plantType.name,
          status: plantType.status || 'Active',
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          code: '',
          name: '',
          status: 'Active',
        });
      }
      setFormErrorMessage(null);
    }
  }, [open, isEditing, plantType]);

  const handleSubmit = async values => {
    setBtnLoading(true);
    setFormErrorMessage(null);

    try {
      let response;
      if (isEditing) {
        response = await plantTypeService.updatePlantType(plantType.id, values);
      } else {
        response = await plantTypeService.addPlantType(values);
      }

      if (response.data) {
        message.success(
          `Plant Type ${isEditing ? 'updated' : 'created'} successfully!`,
        );
        onClose();
      }
    } catch (error) {
      setBtnLoading(false);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.code) {
          setFormErrorMessage(
            Array.isArray(errorData.code)
              ? errorData.code.join(', ')
              : errorData.code,
          );
        } else if (errorData.message) {
          message.error(errorData.message);
        } else if (errorData.non_field_errors) {
          message.error(
            Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors.join(', ')
              : errorData.non_field_errors,
          );
        } else {
          const messages = [];
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              messages.push(`${key}: ${errorData[key].join(', ')}`);
            }
          }
          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        }
      } else if (typeof error === 'string') {
        message.error(error);
      } else if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unknown error occurred.');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFormErrorMessage(null);
    onClose();
  };


  return (
    <Drawer
      title={isEditing ? 'Edit Plant Type' : 'Add Plant Type'}
      width={500}
      onClose={handleClose}
      open={open}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          code: '',
          name: '',
          status: 'Active',
        }}
      >
        <Form.Item
          name="code"
          label="Code"
          rules={[
            { required: true, message: 'Please enter a code!' },
            {
              pattern: /^[A-Z0-9_]+$/,
              message: 'Code must be uppercase letters, numbers, and underscores only!',
            },
            {
              max: 50,
              message: 'Code cannot exceed 50 characters!',
            },
          ]}
          help={formErrorMessage || undefined}
          validateStatus={formErrorMessage ? 'error' : undefined}
        >
          <Input
            placeholder="Enter Code (e.g., PLANT_001)"
            onChange={e => {
              const value = e.target.value.toUpperCase();
              form.setFieldsValue({ code: value });
            }}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, message: 'Please enter a name!' },
            {
              max: 200,
              message: 'Name cannot exceed 200 characters!',
            },
          ]}
        >
          <Input
            placeholder="Enter Plant Type Name"
            onChange={e => {
              const input = e.target;
              const value = input.value;
              const caretPosition = input.selectionStart;
              const formattedValue = value
                .toLowerCase()
                .replace(/\b\w/g, char => char.toUpperCase());
              form.setFieldsValue({ name: formattedValue });
              setTimeout(() => {
                input.setSelectionRange(caretPosition, caretPosition);
              }, 0);
            }}
          />
        </Form.Item>

        

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select a status!' }]}
        >
          <Select 
            placeholder="Select Status"
            getPopupContainer={trigger => trigger.parentElement}
          >
            <Option value="Active">Active</Option>
            <Option value="Inactive">Inactive</Option>
          </Select>
        </Form.Item>

        <div className="flex justify-end" style={{ marginTop: '24px' }}>
          <Button
            onClick={handleClose}
            style={{ marginRight: '8px' }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={btnLoading}
          >
            {isEditing ? 'Update Plant Type' : 'Create Plant Type'}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
}

export default AddEditPlantType;

